import { CloseButton } from "@components/buttons/close";
import {
  SHAutocomplete,
  SHButton,
  SHCheckbox,
  SHDialog,
  SHDotGroup,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { ModelScreenerColumnSettingDTO } from "@models/model-screener/entities/modelScreenerFilter";
import { DialogContent, DialogTitle, useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { useToggle } from "react-use";
import { useFormContext } from "react-hook-form";
import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { MSFilterItemLabel } from "../../../filter/filter-item/label";
import { OverwriteColumnDialog } from "../overwrite-modal";
import { MSColumnSaveModalHelpTextConstants } from "../../constants";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";

type MSColumnSaveModalProps = {
  open: boolean;
  onClose: () => void;
  onSaveColumn: ({
    viewName,
    isUserDefault,
    overwriteId,
  }: {
    viewName: string;
    isUserDefault: boolean;
    overwriteId?: string;
  }) => Promise<boolean>;
};
export const MSColumnSaveModal = ({
  open,
  onClose,
  onSaveColumn,
}: MSColumnSaveModalProps) => {
  const { palette } = useTheme();
  const { getValues } = useFormContext<ModelScreenerColumnSettingDTO>();
  const modelScreenerColumnSettings = useSelector((state: RootState) => {
    return state.modelScreener.columnSettings;
  });

  const nonDefaultColumnOptions = modelScreenerColumnSettings?.filter(
    (x) => !x.isSystemDefault,
  );

  // States
  const [isChecked, setIsChecked] = useToggle(false);
  const [viewName, setViewName] = useState<string>("");
  const [overwriteId, setOverwriteId] = useState<string | undefined>(undefined);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [displayingOptions, setDisplayingOptions] = useState<number>(0);

  // Functions
  const onModalClose = () => {
    onClose();
    setIsChecked(false);
  };

  const handleInputChange = (value: string) => {
    setViewName(value);
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleVerifySubmit = async () => {
    if (!viewName) return;
    let overwriteId = nonDefaultColumnOptions?.find(
      (item) => item?.name === viewName,
    )?.id;
    if (overwriteId) {
      setOverwriteId(overwriteId);
      return;
    }
    await handleOnSubmit();
  };

  const handleOnSubmit = async () => {
    try {
      const saveResult = await onSaveColumn({
        viewName: viewName,
        overwriteId: overwriteId,
        isUserDefault: isChecked,
      });
      if (!saveResult) return;
      onModalClose();
    } finally {
      setOverwriteId(undefined);
      toggleIsSaving(false);
    }
  };

  const isDisabled = useMemo(() => {
    return isEmpty(viewName);
  }, [viewName]);

  return (
    <form>
      <SHDialog
        open={open}
        onClose={onModalClose}
        sx={{
          "& .MuiPaper-root": {
            width: {
              xs: "40%",
              xl: "640px",
            },
            maxWidth: "50%",
            position: "relative",
          },
        }}
      >
        <DialogTitle>
          <SHStack
            sx={{
              position: "absolute",
              top: "22px",
              right: "25px",
            }}
          >
            <CloseButton width="17px" height="17px" onClick={onModalClose} />
          </SHStack>
          <SHTypography fontSize={18} fontWeight={600}>
            Save current view
          </SHTypography>
          <SHTypography fontWeight={400} fontSize={14} disabled>
            {MSColumnSaveModalHelpTextConstants.HelpText}
          </SHTypography>
        </DialogTitle>
        <DialogContent sx={{ alignItems: "center", padding: "20px 0px" }}>
          <SHStack alignItems={"center"} gap={1}>
            <SHStack
              alignItems={"start"}
              width={"100%"}
              direction={"row"}
              gap={0.5}
            >
              <MSFilterItemLabel alignItems={"start"}>
                View name
              </MSFilterItemLabel>
              <SHDotGroup redDot sx={{ mt: "2px" }} />
            </SHStack>

            <SHAutocomplete
              freeSolo
              disabled={isSaving}
              value={undefined}
              options={nonDefaultColumnOptions}
              getOptionLabel={(option) => {
                if (typeof option === "string") return getValues().name;
                return option.name;
              }}
              onInputChange={(_, newValue) => {
                handleInputChange(newValue);
              }}
              filterOptions={(options, state) => {
                const filtered = options.filter((option) =>
                  option.name
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase()),
                );

                setDisplayingOptions(filtered?.length || 0);
                return filtered;
              }}
              renderOption={(props, option) => {
                return (
                  <li
                    {...props}
                    key={option.id}
                    style={{ padding: "10px 10px" }}
                  >
                    <SHStack alignItems={"center"} direction="row" spacing={1}>
                      <SHTypography variant="body3" fontWeight={500}>
                        {option.name}
                      </SHTypography>
                    </SHStack>
                  </li>
                );
              }}
              textFieldProps={{
                placeholder: "Enter view name",
                maxLength: 100,
                onKeyDown: (event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                },
              }}
              sx={{ width: "100%" }}
              PaperComponent={displayingOptions > 0 ? undefined : () => null}
            />
            <SHStack
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              gap={0.5}
            >
              <SHCheckbox
                sx={{
                  width: "20px",
                  marginRight: "5px",
                  marginTop: "-3px",
                  "&:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
                checked={isChecked}
                onClick={() => {
                  handleCheckboxChange();
                }}
              />
              <SHTypography>Save current view as default</SHTypography>
            </SHStack>
            <SHButton
              variant="contained"
              size="extraMedium"
              sx={{
                width: "72%",
                height: "40px",
                paddingX: 3,
                "&:disabled.sh-btn-with-icon": {
                  fill: palette.text.disabled,
                  border: `1px solid ${hexToRGBA(palette.primary.dark, 0.3)}`,
                  backgroundColor: palette.primary[50],
                  color: palette.common.white,
                },
              }}
              disabled={isDisabled || isSaving}
              isLoading={isSaving}
              onClick={() => {
                toggleIsSaving(true);
                setTimeout(() => handleVerifySubmit(), 0);
              }}
            >
              Save view
            </SHButton>
          </SHStack>
        </DialogContent>
      </SHDialog>
      {overwriteId && (
        <OverwriteColumnDialog
          onClose={() => {
            setOverwriteId(undefined);
            toggleIsSaving(false);
          }}
          onSubmit={handleOnSubmit}
        />
      )}
    </form>
  );
};
